import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Cloud,
  Trash2,
  FolderOpen,
  Save,
  Loader2,
  User,
  Calendar,
  Database,
  History,
  ArrowLeftRight,
  RotateCcw,
  FileText,
  AlertCircle,
  Check
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { suggestDraftLabel } from "@/lib/formChoices";
import { useCloudDrafts } from "@/hooks/useCloudDrafts";
import { base44 } from "@/api/base44Client";
import { getSessionDraft } from "@/lib/sessionDraftStore";

const VALS_RED = "#8C1D18";

function formatTimeAgo(dateStr) {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return "just now";
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return dateStr;
  }
}

export default function CloudDraftsPanel({
  formType,
  formData,
  onLoadDraft,
  accentColor = VALS_RED,
  brand = "vals",
  disabled = false,
}) {
  const { user } = useAuth();
  const { drafts, isLoading, error, fetchDrafts, saveDraft, deleteDraft } = useCloudDrafts();

  const [showDialog, setShowDialog] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [guestName, setGuestName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyDraft, setHistoryDraft] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const [comparingVersion, setComparingVersion] = useState(null);
  const [versionDetails, setVersionDetails] = useState({});
  const [showOnlyDiffs, setShowOnlyDiffs] = useState(true);

  // Helper to recursively flatten objects for comparison
  const flattenObject = (obj, prefix = "") => {
    let result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const propName = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === "object" && obj[key] !== null) {
          if (Array.isArray(obj[key])) {
            result[propName] = JSON.stringify(obj[key]);
          } else {
            Object.assign(result, flattenObject(obj[key], propName));
          }
        } else {
          result[propName] = obj[key];
        }
      }
    }
    return result;
  };

  const handleShowHistory = async (draft) => {
    setHistoryDraft(draft);
    setShowHistoryDialog(true);
    setLoadingHistory(true);
    setComparingVersion(null);
    try {
      const data = await base44.versions.list(draft.id);
      setHistoryList(data);
    } catch (err) {
      toast.error("Failed to load version history: " + err.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCompare = async (versionNumber) => {
    try {
      if (versionDetails[versionNumber]) {
        setComparingVersion(versionNumber);
        return;
      }
      
      const details = await base44.versions.get(historyDraft.id, versionNumber);
      let formValues = details.form_data.form_data;
      if (typeof formValues === "string") {
        formValues = JSON.parse(formValues);
      }
      setVersionDetails(prev => ({
        ...prev,
        [versionNumber]: formValues
      }));
      setComparingVersion(versionNumber);
    } catch (err) {
      toast.error("Failed to load version details: " + err.message);
    }
  };

  const handleRestoreVersion = async (versionNumber) => {
    if (!window.confirm(`Are you sure you want to restore to Version ${versionNumber}?`)) {
      return;
    }
    try {
      await base44.versions.restore(historyDraft.id, versionNumber);
      toast.success(`Restored draft to Version ${versionNumber}`);
      
      const details = await base44.versions.get(historyDraft.id, versionNumber);
      let formValues = details.form_data.form_data;
      if (typeof formValues === "string") {
        formValues = JSON.parse(formValues);
      }
      onLoadDraft(formValues);
      
      setShowHistoryDialog(false);
      setShowDialog(false);
    } catch (err) {
      toast.error("Failed to restore version: " + err.message);
    }
  };

  const getDiffs = () => {
    if (!comparingVersion || !versionDetails[comparingVersion]) return [];
    
    const vData = versionDetails[comparingVersion];
    const current = formData || {};
    
    const flatV = flattenObject(vData);
    const flatCurrent = flattenObject(current);
    
    const allKeys = Array.from(new Set([...Object.keys(flatV), ...Object.keys(flatCurrent)]));
    
    const diffList = allKeys.map(key => {
      const valV = flatV[key] !== undefined ? String(flatV[key]) : "";
      const valCurrent = flatCurrent[key] !== undefined ? String(flatCurrent[key]) : "";
      const isDifferent = valV !== valCurrent;
      
      const fieldLabel = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase());
        
      return {
        key,
        fieldLabel,
        valV,
        valCurrent,
        isDifferent
      };
    });
    
    if (showOnlyDiffs) {
      return diffList.filter(d => d.isDifferent);
    }
    return diffList;
  };

  // Load drafts on open
  useEffect(() => {
    if (showDialog) {
      fetchDrafts(formType, brand);
      setDraftLabel("");
      setGuestName("");
    }
  }, [showDialog, formType, brand, fetchDrafts]);

  // Auto-load draft from URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlDraftId = params.get("id") || params.get("draft_id");
    if (urlDraftId) {
      const loadPreloaded = async () => {
        try {
          const draft = getSessionDraft(urlDraftId);
          if (draft && draft.form_data && draft.form_type === formType) {
              // Extract inner form_data if wrapped
              let formValues = draft.form_data;
              if (typeof formValues === "string") {
                formValues = JSON.parse(formValues);
              }
              onLoadDraft(formValues);
              toast.success(`Loaded draft: ${draft.draft_name}`);
          }
        } catch (err) {
          console.error("Failed to auto-load draft from URL:", err);
        }
      };
      loadPreloaded();
    }
  }, [formType, onLoadDraft]);

  const handleOpen = () => {
    if (disabled) return;
    // Name the draft after the client and file the form already knows about. Drafts were
    // saved under whatever was typed, so a list of them read "test", "aa", "draft 2", and
    // which file each belonged to was anybody's guess. Only filled in when the field is
    // empty, so a name already chosen is never overwritten.
    setDraftLabel((current) => current || suggestDraftLabel(formData));
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draftLabel.trim()) {
      toast.error("Please enter a label for this draft");
      return;
    }

    // Determine user name
    let savedBy = "Guest";
    let savedByEmail = null;

    if (user && user.id !== "local-user") {
      savedBy = user.full_name || user.email || "Staff Operator";
      savedByEmail = user.email;
    } else if (guestName.trim()) {
      savedBy = guestName.trim();
    } else {
      toast.error("Please enter your name");
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const urlMatterId = params.get("matter_id");

      await saveDraft({
        formType,
        draftName: draftLabel.trim(),
        savedBy,
        savedByEmail,
        brand,
        formData,
        matter_id: urlMatterId || formData?.matter_number || formData?.matterNumber || formData?.file_ref || formData?.file_no || null
      });
      toast.success("Draft saved for this browser session.");
      setDraftLabel("");
    } catch (err) {
      toast.error(err.message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the draft "${name}"?`)) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteDraft(id, formType, brand);
      toast.success("Draft deleted.");
    } catch (err) {
      toast.error(err.message || "Failed to delete draft");
    } finally {
      setDeletingId(null);
    }
  };

  const handleLoad = (draft) => {
    try {
      onLoadDraft(draft.form_data);
      toast.success(`Loaded draft "${draft.draft_name}"`);
      setShowDialog(false);
    } catch (err) {
      toast.error("Failed to load draft data: " + err.message);
    }
  };

  const isGuestMode = !user || user.id === "local-user";

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={handleOpen}
        className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 transition-all font-medium"
        style={{ color: accentColor, borderColor: `${accentColor}40` }}
      >
        <Cloud className="w-4 h-4" />
        <span>Saved Drafts</span>
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] flex flex-col p-6 overflow-hidden">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold" style={{ color: accentColor }}>
              <Database className="w-5 h-5" />
              <span>Saved Drafts</span>
            </DialogTitle>
            <DialogDescription className="text-slate-500 mt-1">
              Save working drafts only in this browser session. They are not uploaded or shared and clear when the session ends.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-6 pr-1">
            {/* Save Section */}
            <form onSubmit={handleSave} className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                <Cloud className="w-4 h-4" style={{ color: accentColor }} />
                Save Current Progress
              </h4>

              <div className="grid gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    Draft Label (e.g., Client Name / Matter ID)
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter a descriptive name for this draft..."
                    value={draftLabel}
                    onChange={(e) => setDraftLabel(e.target.value)}
                    required
                    className="bg-white border-slate-200 focus:border-slate-400 focus:ring-0 text-sm"
                  />
                </div>

                {isGuestMode ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                      Your Name (Required when not logged in)
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter your name..."
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      required={isGuestMode}
                      className="bg-white border-slate-200 focus:border-slate-400 focus:ring-0 text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100/70 border border-slate-200/50 rounded-lg px-3 py-2">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span>
                      Saving as: <strong className="text-slate-700">{user.full_name}</strong> ({user.email})
                    </span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={saving || !draftLabel.trim()}
                  className="w-full text-white font-medium shadow-sm flex items-center justify-center gap-2"
                  style={{ backgroundColor: accentColor }}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving Draft...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save Draft</span>
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Load Section */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                <FolderOpen className="w-4 h-4 text-slate-500" />
                Your Saved Drafts
              </h4>

              {error && <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{error}</div>}

              {isLoading && drafts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-slate-400 gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
                  <span className="text-xs">Loading your drafts...</span>
                </div>
              ) : drafts.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-slate-100 rounded-xl">
                  <Cloud className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No saved drafts for this form yet.</p>
                  <p className="text-xs text-slate-300 mt-1">Drafts remain only in this browser session.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {drafts.map((draft) => (
                    <div
                      key={draft.id}
                      className="group flex items-center justify-between p-3 border border-slate-100 rounded-xl hover:border-slate-200 hover:bg-slate-50/50 transition-all"
                    >
                      <div className="flex-1 min-w-0 pr-3">
                        <p className="font-medium text-slate-800 text-sm truncate">{draft.draft_name}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {draft.saved_by}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatTimeAgo(draft.updated_at)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition-opacity">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLoad(draft)}
                          className="h-8 text-slate-600 hover:text-slate-800 hover:bg-slate-100 px-2.5 rounded-lg text-xs font-semibold"
                        >
                          Load
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={deletingId === draft.id}
                          onClick={() => handleDelete(draft.id, draft.draft_name)}
                          className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          {deletingId === draft.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-6 overflow-hidden bg-white rounded-2xl shadow-xl border border-slate-100">
          <DialogHeader className="pb-4 border-b border-slate-100 flex flex-row items-start justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold" style={{ color: accentColor }}>
                <History className="w-5 h-5" />
                <span>Version History: {historyDraft?.draft_name}</span>
              </DialogTitle>
              <DialogDescription className="text-slate-500 mt-1">
                Matter {historyDraft?.id} • View details, compare changes side-by-side, and restore past versions.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex gap-6 py-4">
            {/* Left Column: Version list */}
            <div className="w-1/3 flex flex-col border-r border-slate-100 pr-4 overflow-y-auto">
              <h4 className="font-semibold text-slate-800 text-sm mb-3 flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-slate-500" />
                Version Timeline
              </h4>

              {loadingHistory ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
                  <span className="text-xs">Loading history...</span>
                </div>
              ) : historyList.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-xl">
                  <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No versions found.</p>
                  <p className="text-xs text-slate-300 mt-1">Versions are generated when you click Save.</p>
                </div>
              ) : (
                <div className="space-y-2 flex-1">
                  {historyList.map((ver) => (
                    <div
                      key={ver.version_number}
                      className={`p-3 border rounded-xl transition-all cursor-pointer ${
                        comparingVersion === ver.version_number
                          ? "border-slate-400 bg-slate-50"
                          : "border-slate-100 hover:border-slate-200"
                      }`}
                      onClick={() => handleCompare(ver.version_number)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-800 text-sm">
                          Version {ver.version_number}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestoreVersion(ver.version_number);
                          }}
                          className="h-7 text-xs border-slate-200 hover:bg-slate-50 text-slate-600 flex items-center gap-1"
                        >
                          <RotateCcw className="w-3 h-3" />
                          <span>Restore</span>
                        </Button>
                      </div>

                      <div className="mt-2 text-xs text-slate-500 space-y-1">
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          <span className="truncate max-w-[150px]">{ver.user_name || ver.user_id}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>{new Date(ver.timestamp).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Comparison / Details view */}
            <div className="flex-1 flex flex-col min-w-0">
              {comparingVersion ? (
                <div className="flex flex-col h-full min-w-0">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-3">
                    <div className="flex items-center gap-2">
                      <ArrowLeftRight className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-slate-800 text-sm">
                        Comparing Version {comparingVersion} with Current
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer font-medium">
                        <input
                          type="checkbox"
                          checked={showOnlyDiffs}
                          onChange={(e) => setShowOnlyDiffs(e.target.checked)}
                          className="rounded border-slate-300 text-slate-800 focus:ring-0"
                        />
                        <span>Only show changes</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto pr-1">
                    {getDiffs().length === 0 ? (
                      <div className="text-center py-20 bg-slate-50 border border-slate-100 rounded-xl">
                        <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-slate-700">No differences detected</p>
                        <p className="text-xs text-slate-400 mt-1">This version is identical to the current form state.</p>
                      </div>
                    ) : (
                      <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead>
                            <tr className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-100">
                              <th className="p-3 w-1/3">Field</th>
                              <th className="p-3 w-1/3">Version {comparingVersion}</th>
                              <th className="p-3 w-1/3">Current State</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getDiffs().map((diff) => (
                              <tr
                                key={diff.key}
                                className={`border-b border-slate-100/50 hover:bg-slate-50/50 transition-colors ${
                                  diff.isDifferent ? "bg-amber-50/20" : ""
                                }`}
                              >
                                <td className="p-3 font-semibold text-slate-700 truncate max-w-[150px]" title={diff.key}>
                                  {diff.fieldLabel}
                                </td>
                                <td className={`p-3 font-mono break-all max-w-[200px] ${diff.isDifferent ? "text-amber-700 bg-amber-50/40 font-semibold" : "text-slate-500"}`}>
                                  {diff.valV || <span className="text-slate-300 italic">empty</span>}
                                </td>
                                <td className={`p-3 font-mono break-all max-w-[200px] ${diff.isDifferent ? "text-emerald-700 bg-emerald-50/40 font-semibold" : "text-slate-500"}`}>
                                  {diff.valCurrent || <span className="text-slate-300 italic">empty</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6 border-2 border-dashed border-slate-100 rounded-xl">
                  <ArrowLeftRight className="w-10 h-10 text-slate-200 mb-3" />
                  <h5 className="font-semibold text-slate-700 text-sm">Select a Version to Compare</h5>
                  <p className="text-xs text-slate-400 mt-1 max-w-xs">
                    Click any version from the timeline on the left to compare its fields side-by-side with your current edits.
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
